import { Link as MuiLink } from "@mui/material";
import React from "react";
import { PropertyDisplayFormat } from "../../Models/PropertyDisplayFormat";
import { formatDisplayValue } from "../../Utils/displayFormatting";

export type ListItemTemplate = {
  PrimaryTextField?: string;
  PrimaryTextFormat?: PropertyDisplayFormat;
  SecondaryTextField?: string;
  SecondaryTextFormat?: PropertyDisplayFormat;
  SupportingTextField?: string;
  SupportingTextFormat?: PropertyDisplayFormat;
  IconName?: string;
  BadgeField?: string;
  BadgeFormat?: PropertyDisplayFormat;
};

export function getListFieldValue(item: any, field?: string) {
  if (!field) {
    return undefined;
  }

  if (field === "." || field === "$item") {
    return item;
  }

  if (!item || typeof item !== "object") {
    return undefined;
  }

  return field.split(".").reduce((current, segment) => current?.[segment], item);
}

export function formatListFieldValue(
  value: unknown,
  field: string | undefined,
  label: string,
  format: PropertyDisplayFormat | undefined,
  currencyCode: string | undefined,
  phoneNumberFormat: string | undefined,
  maskPii: boolean
) {
  if (value === undefined) {
    return undefined;
  }

  return formatDisplayValue(
    value,
    { format, currencyCode, phoneNumberFormat },
    {
      maskPii,
      fieldPath: field,
      label,
      format,
    }
  );
}

export function renderListFieldValue(
  value: unknown,
  field: string | undefined,
  label: string,
  format: PropertyDisplayFormat | undefined,
  currencyCode: string | undefined,
  phoneNumberFormat: string | undefined,
  maskPii: boolean
): React.ReactNode {
  const displayValue = formatListFieldValue(value, field, label, format, currencyCode, phoneNumberFormat, maskPii);
  if (displayValue === undefined) {
    return undefined;
  }

  if (format === PropertyDisplayFormat.EmailLink) {
    return (
      <MuiLink href={`mailto:${String(value)}`} variant="body2" underline="hover" onClick={(event) => event.stopPropagation()}>
        {displayValue}
      </MuiLink>
    );
  }

  if (format === PropertyDisplayFormat.PhoneLink) {
    return (
      <MuiLink href={`tel:${String(value).replace(/\D/g, "")}`} variant="body2" underline="hover" onClick={(event) => event.stopPropagation()}>
        {displayValue}
      </MuiLink>
    );
  }

  if (format === PropertyDisplayFormat.Link) {
    return (
      <MuiLink href={String(value)} target="_blank" rel="noopener noreferrer" variant="body2" underline="hover" onClick={(event) => event.stopPropagation()}>
        {displayValue}
      </MuiLink>
    );
  }

  return displayValue;
}

export function coerceListItems(value: any, componentLabel: string) {
  if (value === undefined || value === null || value === false) {
    return [];
  }

  const items = Array.isArray(value) ? value : value.Items ?? value.items;
  if (!Array.isArray(items)) {
    throw new Error(`${componentLabel} expression did not return an array or an object with an Items array: ${value}`);
  }

  return items;
}

export function getListItemKey(item: any, keyField: string, index: number): string | number {
  if (item && typeof item === "object") {
    return item[keyField] ?? index;
  }

  return item ?? index;
}

export function startsWithPath(path: string[] | null | undefined, prefix: string[]) {
  if (!path || path.length < prefix.length) {
    return false;
  }

  return prefix.every((segment, index) => path[index] === segment);
}
